// ERP Sync overview (prototype renderERP): header with Resync site, feed-mode strip, delivery-paused banner and alerts,
// KPI tiles, inbound and outbound webhooks, recent resync requests, and the source-of-truth matrix.
// Status and the inbound list poll every 5 s while the page is visible; they also refresh on `hold` and `replica`.

import { useMemo, useState } from 'react';
import { api, type Delivery, type ErpAlert, type ErpStatus } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, Loading, StatusPill } from '../../components/ui.tsx';
import { useAction, useLoad } from '../../hooks.ts';
import { href } from '../../router.ts';
import { useSession } from '../../session.tsx';
import { useRefreshOn } from '../../stream.tsx';
import { fmtLocal, fmtTime, OPERATOR_TZ, relAge } from '../../time.ts';
import { digestHead, ErpHeader, fmtAgeS, human, inboxRes, ModeStrip, num, plural, RefreshBtn, scopeText, usePoll } from './shared.tsx';

const LEVEL_ORDER: Record<ErpAlert['level'], number> = { critical: 0, warning: 1, info: 2 };

/* ---------- resync controls ---------- */

function ResyncPanel({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const s = useSession();
  const sites = useLoad((signal) => api.sites(undefined, signal), []);
  const [siteId, setSiteId] = useState('');
  const [note, setNote] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const send = useAction(async (scope: { kind: 'site'; site_id: string } | { kind: 'all' }) => {
    const r = await api.erpResync(scope, note.trim() || undefined);
    setSent(r.requestId);
    setConfirmAll(false);
    setNote('');
    onSent();
    return r;
  });
  const list = (sites.data ?? []).filter((x) => x.active).sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Card
      title={<><Icon name="sync" />Ask the ERP to resend</>}
      right={
        <button type="button" className="btn sm ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="note">The SOC sends resync.requested; the ERP pushes the current state back as ordinary messages, then a digest (contract §7.1).</div>
      <div className="filters">
        <label className="field inline">
          <span className="lbl">Site</span>
          <select value={siteId} onChange={(e) => setSiteId(e.target.value)} aria-label="Site to resync" disabled={!sites.data}>
            <option value="">{sites.data ? 'Choose a site…' : 'Loading sites…'}</option>
            {list.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} · {x.companyName}
              </option>
            ))}
          </select>
        </label>
        <label className="field grow">
          <span className="lbl">Note (optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Why, for the ERP team's logs" />
        </label>
        <button type="button" className="btn pri" disabled={!siteId || send.busy} onClick={() => void send.run({ kind: 'site', site_id: siteId })}>
          <Icon name="send" />
          {send.busy ? 'Sending…' : 'Resync site'}
        </button>
        {s.isAdmin && (
          <button type="button" className="btn" disabled={send.busy || confirmAll} onClick={() => setConfirmAll(true)}>
            Full resync…
          </button>
        )}
      </div>
      {sites.error ? <InlineError error={sites.error} /> : null}
      {confirmAll && (
        <div className="confirmbar">
          <span>Ask the ERP for every entity? It answers with the whole dataset and a digest. The SOC sends at most one full request at a time.</span>
          <button type="button" className="btn pri" disabled={send.busy} onClick={() => void send.run({ kind: 'all' })}>
            {send.busy ? 'Sending…' : 'Yes, full resync'}
          </button>
          <button type="button" className="btn" disabled={send.busy} onClick={() => setConfirmAll(false)}>
            Back
          </button>
        </div>
      )}
      <InlineError error={send.error} />
      {sent && (
        <div className="note">
          <Icon name="check" /> Sent <span className="mono">{sent}</span>. Its answer shows under Recent resync requests and in Digests.
        </div>
      )}
    </Card>
  );
}

/* ---------- alerts and paused banner ---------- */

function PausedBanner({ st, onDone }: { st: ErpStatus; onDone: () => void }) {
  const s = useSession();
  const resume = useAction(async () => {
    await api.erpResumeDelivery();
    onDone();
  });
  const p = st.delivery.paused;
  if (!p.paused) return null;
  return (
    <div className="banner bad">
      <Icon name="pause" />
      <span>
        <b>SOC → ERP delivery is paused.</b> {p.reason ?? 'The ERP refused a message.'}
        {p.at ? <span className="note"> Since {fmtLocal(p.at)}.</span> : null} Messages wait in the delivery log until it resumes.
      </span>
      {s.isAdmin ? (
        <button type="button" className="btn sm" disabled={resume.busy} onClick={() => void resume.run()}>
          <Icon name="play" />
          {resume.busy ? 'Resuming…' : 'Resume'}
        </button>
      ) : (
        <span className="note">An admin resumes it after fixing the cause.</span>
      )}
      <InlineError error={resume.error} />
    </div>
  );
}

function Alerts({ alerts }: { alerts: ErpAlert[] }) {
  const rows = alerts.filter((a) => a.code !== 'delivery_paused').sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
  if (!rows.length) return null;
  return (
    <ul className="erp-alerts" aria-label="Needs attention">
      {rows.map((a, i) => (
        <li key={`${a.code}-${i}`} className={`lv-${a.level}`}>
          <span className="lv">{a.level}</span>
          <span>{a.text}</span>
          {(a.code === 'holds' || a.code === 'dead_letters') && (
            <a href={href.imports()} className="small">
              Handle in Imports
            </a>
          )}
          {(a.code === 'delivery_dead' || a.code === 'delivery_lag') && (
            <a href={href.erpSync('deliveries')} className="small">
              Delivery log
            </a>
          )}
          {a.code === 'inbox_failed' && (
            <a href={href.erpSync('inbox')} className="small">
              Inbox
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

/* ---------- KPI tiles ---------- */

function webhookHealth(st: ErpStatus): { text: string; tone: 'ok' | 'bad' | 'warn' | 'dim'; sub: string } {
  const recentAuth = st.receiver.lastAuthFailureAt && Date.now() - new Date(st.receiver.lastAuthFailureAt).getTime() < 3_600_000;
  const host = st.delivery.host ? ` · ERP ${st.delivery.host}` : '';
  if (st.delivery.paused.paused) return { text: 'Delivery paused', tone: 'bad', sub: 'SOC → ERP stopped; see the banner above' };
  if (recentAuth) return { text: 'Signature failures', tone: 'bad', sub: st.receiver.lastAuthFailure ?? 'bad signature' };
  if (!st.receiver.configured || !st.delivery.configured) {
    const missing = [!st.receiver.configured && 'ERP → SOC secret', !st.delivery.configured && 'SOC → ERP hook URL or secret'].filter(Boolean).join(', ');
    return { text: 'Not configured', tone: st.feed.mode === 'off' ? 'dim' : 'warn', sub: `Missing: ${missing}` };
  }
  if (st.feed.mode === 'off') return { text: 'Ready, feed off', tone: 'dim', sub: `Signed HMAC both ways${host}` };
  return { text: st.feed.mode === 'live' ? 'Healthy both ways' : 'Healthy (shadow)', tone: 'ok', sub: `Signed HMAC${host}` };
}

function Tiles({ st }: { st: ErpStatus }) {
  const h = webhookHealth(st);
  const by = st.receiver.byStatus24h;
  const applied = by.applied ?? 0;
  const stale = by.skipped_stale ?? 0;
  const same = by.skipped_same ?? 0;
  const failed = (by.failed ?? 0) + (by.dead ?? 0);
  const d = st.lastDigest;
  const dg = d ? digestHead(d.comparedAt ?? d.asOf, d.summary) : null;
  const deadTotal = st.deadLetters + st.delivery.dead;
  return (
    <div className="tiles">
      <div className="tilek">
        <span className="lbl">Webhooks</span>
        <b className={`tk t-${h.tone}`}>{h.text}</b>
        <small>{h.sub}</small>
      </div>
      <div className="tilek">
        <span className="lbl">Events applied · 24 h</span>
        <b>{num(applied)}</b>
        <small>
          {num(stale)} stale skipped · {num(same)} unchanged{failed ? ` · ${num(failed)} failed` : ''}
        </small>
      </div>
      <div className={`tilek${deadTotal ? ' warnk' : ''}`}>
        <span className="lbl">Dead letters</span>
        <b>{num(deadTotal)}</b>
        <small>
          {st.deadLetters > 0 || st.holds > 0 ? (
            <a href={href.imports()}>
              {num(st.deadLetters)} inbound · {plural(st.holds, 'held change')}
            </a>
          ) : (
            <>{num(st.deadLetters)} inbound · {plural(st.holds, 'held change')}</>
          )}
          {st.delivery.dead ? <> · <a href={href.erpSync('deliveries')}>{num(st.delivery.dead)} outbound</a></> : null}
          {st.parked ? ` · ${num(st.parked)} parked` : ''}
        </small>
      </div>
      <div className="tilek">
        <span className="lbl">{d && d.requestId ? 'Last reconciliation' : 'Nightly reconciliation'}</span>
        <b className={`tk${dg && !dg.ok ? ' t-warn' : ''}`}>{dg ? dg.head : 'None yet'}</b>
        <small>
          {dg ? (
            <a href={href.erpSync('digests')}>ERP digest: {dg.tail}</a>
          ) : (
            'No digest compared yet'
          )}
        </small>
      </div>
    </div>
  );
}

/* ---------- inbound / outbound ---------- */

function Inbound({ st, load }: { st: ErpStatus; load: ReturnType<typeof useInbox> }) {
  const rows = load.data;
  const r = st.receiver;
  const by = Object.entries(r.byStatus24h).sort((a, b) => b[1] - a[1]);
  return (
    <Card
      title={<><Icon name="link" />Inbound webhooks · ERP → SOC</>}
      right={
        <a href={href.erpSync('inbox')} className="small">
          Inbox
        </a>
      }
    >
      <div className="erp-kv">
        <span>
          <span className="lbl">To apply</span> <b className={r.pending ? 'warn-t' : ''}>{num(r.pending)}</b>
        </span>
        <span>
          <span className="lbl">Received 24 h</span> <b>{num(r.received24h)}</b>
        </span>
        <span>
          <span className="lbl">Last received</span> <b title={fmtLocal(r.lastReceivedAt)}>{r.lastReceivedAt ? relAge(r.lastReceivedAt) : '—'}</b>
        </span>
        <span>
          <span className="lbl">Last signature failure</span>{' '}
          <b className={r.lastAuthFailureAt ? 'warn-t' : ''} title={r.lastAuthFailure ?? undefined}>
            {r.lastAuthFailureAt ? relAge(r.lastAuthFailureAt) : 'none'}
          </b>
        </span>
      </div>
      {by.length > 0 && (
        <div className="note">
          24 h: {by.map(([k, n]) => `${num(n)} ${k.replace(/_/g, ' ')}`).join(' · ')}
        </div>
      )}
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load recent messages" />
      ) : !rows ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>No ERP messages received yet.</Empty>
      ) : (
        <div className="tw">
          <table className="erp-t">
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Event</th>
                <th scope="col">Entity</th>
                <th scope="col" className="num">Ver</th>
                <th scope="col">Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.eventId}>
                  <td className="mono dim nowrap" title={fmtLocal(m.receivedAt)}>
                    {m.receivedAt ? fmtTime(m.receivedAt, OPERATOR_TZ) : '—'}
                  </td>
                  <td className="mono">{m.eventType}</td>
                  <td className="ell" title={`${m.entityType ?? ''} ${m.entityId ?? ''}`}>
                    {m.entityType ? (
                      <>
                        {m.entityType} <span className="mono dim">{m.entityId}</span>
                      </>
                    ) : (
                      <span className="dim">—</span>
                    )}
                  </td>
                  <td className="mono dim num">v{m.version}</td>
                  <td>
                    <span className={`res ${inboxRes(m.status)}`} title={m.reason ?? undefined}>
                      {human(m.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="note">Applied only if the version is newer, so replays and out-of-order delivery are safe. Deletes deactivate and keep history.</div>
    </Card>
  );
}

const OUT_TYPES: readonly [string, string][] = [
  ['event.closed', 'Billing and customer reports'],
  ['device.health_changed', 'Service tickets'],
  ['drift.detected', 'Install team'],
  ['data_issue.reported', 'Contact owner'],
  ['resync.requested', 'ERP pushes the entities back'],
];

function Outbound({ st, deliveries }: { st: ErpStatus; deliveries: Delivery[] | undefined }) {
  const d = st.delivery;
  const counts = useMemo(() => {
    const since = Date.now() - 86_400_000;
    const m = new Map<string, number>();
    for (const r of deliveries ?? []) {
      if (r.createdAt && new Date(r.createdAt).getTime() >= since) m.set(r.eventType, (m.get(r.eventType) ?? 0) + 1);
    }
    return m;
  }, [deliveries]);
  const extra = [...counts.keys()].filter((k) => !OUT_TYPES.some(([t]) => t === k));
  const rows: [string, string][] = [...OUT_TYPES, ...extra.map((k): [string, string] => [k, ''])];
  return (
    <Card
      title={<><Icon name="send" />Outbound webhooks · SOC → ERP</>}
      right={
        <a href={href.erpSync('deliveries')} className="small">
          Delivery log
        </a>
      }
    >
      <div className="erp-kv">
        <span>
          <span className="lbl">Delivery</span>{' '}
          <b className={d.paused.paused ? 'bad-t' : d.on ? 'ok-t' : ''}>{d.paused.paused ? 'paused' : d.on ? 'on' : 'off'}</b>
        </span>
        <span>
          <span className="lbl">Pending</span> <b>{num(d.pending)}</b>
        </span>
        <span>
          <span className="lbl">Oldest pending</span> <b className={d.oldestPendingS > 300 ? 'warn-t' : ''}>{d.pending ? fmtAgeS(d.oldestPendingS) : '—'}</b>
        </span>
        <span>
          <span className="lbl">Dead</span> <b className={d.dead ? 'bad-t' : ''}>{num(d.dead)}</b>
        </span>
        <span>
          <span className="lbl">Sent 24 h</span> <b>{num(d.sent24h)}</b>
        </span>
        <span>
          <span className="lbl">Held until cutover</span> <b>{num(d.heldUntilCutover)}</b>
        </span>
      </div>
      <div>
        {rows.map(([t, desc]) => (
          <div key={t} className="trow">
            <span className="mono">{t}</span>
            <span className="sp" />
            <span className="muted small">{desc}</span>
            <span className="mono erp-cnt" title="Created in the last 24 hours">
              {deliveries ? `${num(counts.get(t) ?? 0)} · 24 h` : '…'}
            </span>
          </div>
        ))}
      </div>
      <div className="note">
        {!d.on
          ? `Delivery switches on at promotion; until then messages are held (${num(d.heldUntilCutover)} so far). resync.requested still goes out in shadow mode.`
          : d.pending === 0 && d.dead === 0
            ? 'All delivered (2xx). '
            : `${plural(d.pending, 'message')} pending, ${num(d.dead)} dead. `}
        {d.on ? 'Retries with backoff for up to 24 h, then dead-letter in the delivery log.' : ''}
      </div>
    </Card>
  );
}

/* ---------- resync requests ---------- */

function ResyncRequests({ load }: { load: ReturnType<typeof useResyncRequests> }) {
  const rows = load.data;
  return (
    <Card title={<><Icon name="sync" />Recent resync requests</>} right={<RefreshBtn onClick={load.reload} />}>
      {load.error && !rows ? (
        <ErrorBox error={load.error} onRetry={load.reload} title="Could not load resync requests" />
      ) : !rows ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty>No resync requests yet.</Empty>
      ) : (
        <div className="tw">
          <table className="erp-t">
            <thead>
              <tr>
                <th scope="col">Request</th>
                <th scope="col">Reason</th>
                <th scope="col">Scope</th>
                <th scope="col">By</th>
                <th scope="col">Status</th>
                <th scope="col" className="num">Replies</th>
                <th scope="col">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 10).map((r) => (
                <tr key={r.requestId}>
                  <td className="mono nowrap">{r.requestId}</td>
                  <td className="small">
                    {human(r.reason)}
                    {r.note && <div className="note ell-n" title={r.note}>{r.note}</div>}
                  </td>
                  <td className="small">{scopeText(r.scope)}</td>
                  <td className="small ell">{r.requestedBy?.user?.display_name ?? 'system'}</td>
                  <td>
                    <StatusPill status={r.status} />
                  </td>
                  <td className="mono num">{num(r.replies)}</td>
                  <td className="mono nowrap" title={fmtLocal(r.createdAt)}>
                    {relAge(r.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ---------- source of truth (prototype matrix) ---------- */

const OWN_LABEL = { erp: 'ERP', soc: 'SOC', der: 'SOC · derived', idp: 'Entra ID' } as const;
const MATRIX: readonly [string, keyof typeof OWN_LABEL, string][] = [
  ['Company / customer', 'erp', 'Read-only replica'],
  ['Site: name, address, geo, time zone, plan', 'erp', 'Read-only replica'],
  ['Site map and device placement', 'erp', 'Read-only, drawn in console map'],
  ['Cameras incl. NX IDs and analytics ID', 'erp', 'Read-only replica'],
  ['Speakers, relays, I/O modules', 'erp', 'Read-only replica (recommended)'],
  ['Customer contacts', 'erp', 'Read-only; SOC stores only references'],
  ['Contracted monitoring hours', 'erp', 'Read-only; SOC adds temporary overrides'],
  ['Call procedure (order, rules)', 'soc', 'Editable; points to ERP contact IDs'],
  ['Missions, special instructions', 'soc', 'Editable'],
  ['AI risk profile, pain points, tours', 'soc', 'Editable'],
  ['Authorities directory', 'soc', 'Editable (open question)'],
  ['Device health', 'der', 'Measured from NX, reported to ERP'],
  ['Alarms, incidents, dispositions, audit', 'soc', 'Published to ERP as summary events'],
  ['Users and identity', 'idp', 'Entra ID; SOC owns roles and skills'],
];

function SourceOfTruth() {
  return (
    <Card title={<><Icon name="db" />Source of truth</>}>
      <div className="tw">
        <table className="erp-t">
          <thead>
            <tr>
              <th scope="col">Data</th>
              <th scope="col">Owner</th>
              <th scope="col">In the SOC</th>
            </tr>
          </thead>
          <tbody>
            {MATRIX.map(([d, o, h]) => (
              <tr key={d}>
                <td>{d}</td>
                <td>
                  <span className={`own ${o}`}>{OWN_LABEL[o]}</span>
                </td>
                <td className="muted">{h}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ---------- tab ---------- */

const useInbox = () => useLoad((signal) => api.erpInbox({ limit: 15 }, signal), []);
const useResyncRequests = () => useLoad((signal) => api.erpResyncRequests(signal), []);

export function OverviewTab() {
  const status = useLoad((signal) => api.erpStatus(signal), []);
  const inbox = useInbox();
  const deliveries = useLoad((signal) => api.erpDeliveries({ limit: 1000 }, signal), []);
  const requests = useResyncRequests();
  const [resyncOpen, setResyncOpen] = useState(false);
  const fast = () => {
    status.reload();
    inbox.reload();
  };
  usePoll(fast, 5_000);
  usePoll(() => {
    deliveries.reload();
    requests.reload();
  }, 30_000);
  useRefreshOn(['hold', 'replica'], fast, 800);
  const st = status.data;

  return (
    <div className="page">
      <ErpHeader
        tab="overview"
        sub="Signed webhooks both ways: ERP → SOC master data, SOC → ERP operational events. No shared database, no API reads. One owner per field."
        actions={
          <button type="button" className={`btn${resyncOpen ? ' on' : ''}`} onClick={() => setResyncOpen((v) => !v)} aria-expanded={resyncOpen}>
            <Icon name="sync" />
            Resync site
          </button>
        }
      />
      {resyncOpen && (
        <ResyncPanel
          onClose={() => setResyncOpen(false)}
          onSent={() => {
            requests.reload();
            deliveries.reload();
            status.reload();
          }}
        />
      )}
      {status.error && !st ? (
        <ErrorBox error={status.error} onRetry={status.reload} title="Could not load ERP sync status" />
      ) : !st ? (
        <Loading label="Loading ERP sync status…" />
      ) : (
        <>
          <ModeStrip feed={st.feed} link />
          <PausedBanner st={st} onDone={status.reload} />
          <Alerts alerts={st.alerts} />
          <Tiles st={st} />
          <div className="cols">
            <Inbound st={st} load={inbox} />
            <Outbound st={st} deliveries={deliveries.data} />
          </div>
          <ResyncRequests load={requests} />
          <SourceOfTruth />
        </>
      )}
    </div>
  );
}
